import { SidebarProvider } from "@/components/ui/sidebar";
import { ChatKitProvider } from "@/features/assistant/context/ChatKitContext";
import { SupaFAB } from "../SupaFAB";
import { UserChatKitButton } from "../UserChatKitButton";
import { UserSidebar, UserSidebarProps } from "./UserSidebar";

interface UserLayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  sidebarProps?: UserSidebarProps;
}

/**
 * UserLayout — Main layout wrapper for all user-facing pages
 * 
 * Mobile-First Architecture:
 * - SupaFAB: Universal navigation and quick actions on mobile - AI Assistant, Wallet, Activity, Settings, Help
 * - Sidebar: Desktop only (≥lg) - Full navigation menu
 * - UserChatKitButton: AI Assistant floating button on desktop only
 * 
 * Layout Structure:
 * - Desktop: Sidebar (left) + Content (right) + UserChatKitButton (floating)
 * - Mobile: Content (full width) + SupaFAB (floating navigation)
 * 
 * Safe Areas:
 * - Content area respects notch/home indicator
 * - SupaFAB positioned above safe area
 */
export function UserLayout({ children, header, sidebarProps }: UserLayoutProps) {
  return (
    <ChatKitProvider>
      <SidebarProvider defaultOpen>
        <div className="flex min-h-screen w-full bg-background">
          {/* Desktop Sidebar - hidden on mobile */}
          <UserSidebar className="hidden lg:block" {...sidebarProps} />

          <div className="flex flex-1 flex-col">
            {/* Header area (legacy support) */}
            {header}

            {/* Main Content with safe-area padding for floating controls */}
            <div className="flex-1 pb-[calc(5.5rem+env(safe-area-inset-bottom))] lg:pb-6">
              {children}
            </div>

            {/* SupaFAB - All-in-one navigation and quick actions for mobile */}
            {/* Mobile only (<lg), floating above content with safe area support */}
            <SupaFAB />

            {/* AI Support Chat - ChatKit-powered floating widget (desktop only) */}
            <UserChatKitButton />
          </div>
        </div>
      </SidebarProvider>
    </ChatKitProvider>
  );
}
